import { Logger } from 'bernie-logger';

import { STORE_EVENTS } from 'stores/events/store-events';

import {
  DestinationPageLink,
  GalleryViewModel,
  HotelDetailsViewModel,
  HotelMapModel,
  LocationSummaryViewModel,
  LocationViewModel,
  MapMarker,
  NavigationBarTabModel,
  OverviewViewModel,
  WhatsAroundModuleViewModel,
  WhatsAroundTransportation
} from 'models';

import { adaptAmenities, hasAmenities, updateAdditionalInfo } from './property-info-amenities-adapter';

export const adaptPropertyInfoDetails = (
  response,
  useBexApiPdpMigrationFeatures,
  useRolledOutFeatures,
  logger: Logger
): HotelDetailsViewModel => {
  const propertyInfo = response?.data?.propertyInfo;
  const propertyInfoDetails = propertyInfo?.pwaOnlyBffResponse?.details;
  const hotel = propertyInfoDetails ? JSON.parse(propertyInfoDetails) : response;

  if (propertyInfo && useRolledOutFeatures) {
    // rolled out features
    hotel.gallery = adaptGallery(propertyInfo.propertyGallery) ?? logMissingFieldAndFallback(hotel.gallery, 'gallery', logger);

    if (hasWhatsAroundModuleData(response)) {
      hotel.locationInfo.whatsAroundModule = adaptWhatsAround(response);
    } else {
      logMissingField('summary.location.whatsAround', logger);
    }

    if (hasNearbyPOIs(response)) {
      hotel.locationInfo.locationSummary = adaptLocationSummary(response);
    } else {
      logMissingField('summary.nearbyPOIs', logger);
    }

    if (hasMapModel(response)) {
      hotel.mapModel = adaptMapModel(response);
    } else {
      logMissingField('summary.map', logger);
    }

    const hotelName = propertyInfo.summary?.name ?? logMissingFieldAndFallback(hotel.hotelName, 'summary.name', logger);
    hotel.hotelName = hotelName;
    hotel.name = hotelName;

    hotel.starRatingIcon =
      propertyInfo.summary?.starRatingIcon?.id ?? logMissingFieldAndFallback(hotel.starRatingIcon, 'summary.starRatingIcon', logger);

    hotel.stars =
      propertyInfo.summary?.overview?.starRating ?? logMissingFieldAndFallback(hotel.stars, 'summary.overview.starRating', logger);

    hotel.overview = adaptOverview(propertyInfo) ?? logMissingFieldAndFallback(hotel.overview, 'summary.overview', logger);

    hotel.propertyTypeBadge =
      propertyInfo.summary?.propertyTypeBadge ?? logMissingFieldAndFallback(hotel.propertyTypeBadge, 'summary.propertyTypeBadge', logger);

    hotel.vipMessage =
      propertyInfo.summary?.overview?.vipMessaging ?? logMissingFieldAndFallback(hotel.vipMessage, 'summary.overview.vipMessaging', logger);

    hotel.feedbackModel = {
      feedbackText:
        propertyInfo.feedback?.action?.text ??
        logMissingFieldAndFallback(hotel.feedbackModel?.feedbackText, 'feedbackModel.feedbackText', logger)
    };
  }

  if (propertyInfo && useBexApiPdpMigrationFeatures) {
    hotel.teleSalesNumber =
      propertyInfo.summary?.telesalesPhoneNumber ??
      logMissingFieldAndFallback(hotel.teleSalesNumber, 'summary.telesalesPhoneNumber', logger);

    if (hasAmenities(propertyInfo)) {
      hotel.amenities = adaptAmenities(propertyInfo.summary.amenities);
      hotel.additionalInfo = updateAdditionalInfo(hotel.additionalInfo, propertyInfo.summary.amenities?.amenities);
    } else {
      logMissingField('summary.amenities', logger);
    }

    // When removing adapt code after PDP migration finished, could set configs on the view:
    // https://github.expedia.biz/Brand-Expedia/shopping-pwa/blob/master/src/common/components/infosite/rooms-and-rates/index.tsx#L482
    // Example on hotel landing page:
    // https://github.expedia.biz/Brand-Expedia/shopping-pwa/blob/master/src/common/views/hotel/home.jsx#L48
    const searchOptions = {
      displayFields: ['dateRange', 'rooms'],
      ignoreFields: ['startDate', 'endDate', 'regionId', 'x_pwa']
    };
    hotel.changeSearchDisplayOptions = JSON.parse(JSON.stringify(searchOptions));

    hotel.navigationBarTabList =
      adaptNavigationBarTabList(propertyInfo) ??
      logMissingFieldAndFallback(hotel.navigationBarTabList, 'summary.navigationBarTabs', logger);
  }

  return hotel;
};

function hasNearbyPOIs(response: any) {
  return response?.data?.propertyInfo?.summary?.nearbyPOIs;
}

export const adaptGallery = (propertyGallery): GalleryViewModel => {
  if (!propertyGallery) {
    return propertyGallery;
  }
  const images = propertyGallery.images;
  const categories = propertyGallery.categories;

  return {
    images: images && (images as any[]).map(mapImages),
    categories:
      categories &&
      (categories as any[]).map(propertyGalleryCategory => ({
        id: propertyGalleryCategory.id,
        name: propertyGalleryCategory.name,
        images: propertyGalleryCategory.images && (propertyGalleryCategory.images as any[]).map(mapImages)
      }))
  };
};

export const mapImages = (propertyImage: any) =>
  propertyImage && {
    alt: propertyImage.alt,
    caption: propertyImage.image?.description,
    src: propertyImage.image?.url,
    thumbnailSrc: propertyImage.thumbnail?.url,
    fallBackImage: propertyImage.fallbackImage?.url,
    subjectId: propertyImage.subjectId
  };

function logMissingFieldAndFallback(fallbackValue, fieldName, logger) {
  if (fallbackValue) {
    logMissingField(fieldName, logger);
  }

  return fallbackValue;
}

function logMissingField(fieldName, logger) {
  logger.logEvent(STORE_EVENTS.BEX_API_PDP_MIGRATION_MISSING_FIELD, `${fieldName} is missing from propertyInfo`);
}

export function hasWhatsAroundModuleData(response: any) {
  return response.data?.propertyInfo?.summary?.location?.whatsAround;
}

export function adaptWhatsAround(response: any): WhatsAroundModuleViewModel {
  const whatsAround = response.data.propertyInfo.summary.location.whatsAround;
  const nearbyPOIs = whatsAround.nearbyPOIs.find(item => item.icon.id === 'place');
  const transportation = whatsAround.nearbyPOIs.find(item => item.icon.id === 'train');
  const restaurants = whatsAround.nearbyPOIs.find(item => item.icon.id === 'local_dining');

  return {
    destinationPageLink: whatsAround.editorial.crossLink ? adaptLodgingLinkMessage(whatsAround.editorial.crossLink) : null,
    locationEditorial: whatsAround.editorial.content[0],
    nearbyPOIs: nearbyPOIs ? adaptNearbyPOIs(nearbyPOIs.items) : [],
    nearbyTransportation: transportation ? adaptNearbyTransportation(transportation.items) : [],
    neighborhood: whatsAround.editorial.title,
    restaurants: restaurants ? restaurants.items.map(item => item.text) : [],
    subtitlePOIs: nearbyPOIs ? nearbyPOIs.title : '',
    subtitleRestaurants: restaurants ? restaurants.title : '',
    subtitleTransportations: transportation ? transportation.title : '',
    title: whatsAround.title,
    mapImageUrl: whatsAround.staticMapSrc
  } as WhatsAroundModuleViewModel;
}

export function adaptLodgingLinkMessage(lodgingLinkMessage): DestinationPageLink {
  return {
    text: lodgingLinkMessage.value,
    url: lodgingLinkMessage.link.url
  } as DestinationPageLink;
}

export function adaptNearbyPOIs(propertyInfoItems): LocationViewModel[] {
  return propertyInfoItems.map(
    item =>
      ({
        name: item.text,
        distance: item.moreInfo,
        category: item.icon.id,
        accessibleCategory: item.icon.description
      } as LocationViewModel)
  );
}

export function adaptNearbyTransportation(propertyInfoItems): WhatsAroundTransportation[] {
  return propertyInfoItems.map(
    item =>
      ({
        category: item.icon.id,
        content: item.text
      } as WhatsAroundTransportation)
  );
}

export function adaptLocationSummary(response: any): LocationSummaryViewModel {
  const nearbyPOIs = response.data.propertyInfo.summary.nearbyPOIs;

  return {
    title: nearbyPOIs.title,
    locations: adaptNearbyPOIs(nearbyPOIs.items)
  } as LocationSummaryViewModel;
}

function hasMapModel(response: any) {
  return response?.data?.propertyInfo?.summary?.map;
}

export function adaptMapModel(response: any): HotelMapModel {
  const mapModel = response.data.propertyInfo.summary.map;

  return {
    title: mapModel.title,
    zoom: mapModel.zoom,
    center: {
      lat: mapModel.center.latitude,
      lng: mapModel.center.longitude
    },
    markers: adaptPOIs(mapModel.markers),
    bounds: {
      ne: {
        lat: mapModel.bounds.northeast.latitude,
        lng: mapModel.bounds.northeast.longitude
      },
      sw: {
        lat: mapModel.bounds.southwest.latitude,
        lng: mapModel.bounds.southwest.longitude
      }
    },
    staticMapSrc: mapModel.staticMapSrc
  } as HotelMapModel;
}

function adaptPOIs(POIs): MapMarker[] {
  return POIs.map(
    POI =>
      ({
        position: {
          lat: POI.mapMarker.latLong.latitude,
          lng: POI.mapMarker.latLong.longitude
        },
        sheet: {
          id: POI.id,
          title: POI.title,
          subtitle: POI.subtitle
        },
        icon: POI.mapMarker.icon
      } as MapMarker)
  );
}

export function adaptOverview(propertyInfo: any): OverviewViewModel {
  const location = propertyInfo?.summary?.location;
  if (!location) {
    return null;
  }

  return {
    addressLine: location.address?.addressLine,
    latitude: location.coordinates?.latitude,
    longitude: location.coordinates?.longitude,
    staticMapSrc: location.overviewStaticMapSrc,
    streetAddress: location.address?.firstAddressLine,
    city: location.address?.city,
    state: location.address?.province,
    countryCode: location.address?.countryCode
  };
}

export function adaptNavigationBarTabList(propertyInfo: any): NavigationBarTabModel[] {
  const navigationBarTabs = propertyInfo?.summary?.navigationBarTabs;
  if (!navigationBarTabs) {
    return null;
  }

  return navigationBarTabs.map(
    tab =>
      ({
        tabSection: tab.sectionName,
        localizedTabName: tab.localizedName
      } as NavigationBarTabModel)
  );
}
